import { claimQueueManager } from "@/services/claim-queue/claim-queue-manager";
import type { MemorySample } from "@/services/monitoring/perf-metrics-monitor/perf-metrics-monitor";
import { LAST_APP_OPEN_KEY } from "@/services/monitoring/startup-metrics/constants";
import { cacheManager } from "@/services/pwa/cache-manager/cache-manager";
import { storageManager } from "@/services/storage/storage/storage";
import { captureException } from "@/utils/sentry";
import * as Sentry from "@sentry/react";

type PerformanceWithMemory = Performance & { memory?: MemorySample };

export type StartupMetricsEnv = {
  getPerformance: () => PerformanceWithMemory;
  getNavigator: () => Pick<Navigator, "storage" | "serviceWorker">;
  getLocalStorage: () => Pick<Storage, "getItem" | "setItem">;
  getCacheStorage: () => CacheStorage | undefined;
};

const defaultEnv: StartupMetricsEnv = {
  getPerformance: () => performance as PerformanceWithMemory,
  getNavigator: () => navigator,
  getLocalStorage: () => localStorage,
  getCacheStorage: () => (typeof caches !== "undefined" ? caches : undefined),
};

function readHeap(perf: PerformanceWithMemory): {
  usedMb: number;
  limitMb: number;
} {
  const mem = perf.memory;
  if (!mem) {
    return { usedMb: -1, limitMb: -1 };
  }
  return {
    usedMb: mem.usedJSHeapSize / 1024 / 1024,
    limitMb: mem.jsHeapSizeLimit / 1024 / 1024,
  };
}

async function readStorage(
  nav: Pick<Navigator, "storage">
): Promise<{ usedMb: number; quotaMb: number }> {
  if (!nav.storage?.estimate) {
    return { usedMb: 0, quotaMb: 0 };
  }
  const estimate = await nav.storage.estimate();
  return {
    usedMb: (estimate.usage ?? 0) / 1024 / 1024,
    quotaMb: (estimate.quota ?? 0) / 1024 / 1024,
  };
}

function readNavTiming(perf: Performance): { loadMs: number; fetchMs: number } {
  try {
    const [nav] = perf.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    if (!nav) {
      return { loadMs: -1, fetchMs: -1 };
    }
    return {
      loadMs: nav.loadEventEnd - nav.startTime,
      fetchMs: nav.responseStart - nav.requestStart,
    };
  } catch {
    return { loadMs: -1, fetchMs: -1 };
  }
}

function readHoursSinceLastOpen(storage: Pick<Storage, "getItem">): number {
  const raw = storage.getItem(LAST_APP_OPEN_KEY);
  if (!raw) {
    return -1;
  }
  return (Date.now() - parseInt(raw, 10)) / 1000 / 60 / 60;
}

async function readCachedItemCount(
  cacheStorage: CacheStorage | undefined,
  cacheNames: string[]
): Promise<number> {
  if (!cacheStorage) {
    return 0;
  }
  const counts = await Promise.all(
    cacheNames.map(async (name) => {
      const cache = await cacheStorage.open(name);
      const keys = await cache.keys();
      return keys.length;
    })
  );
  return counts.reduce((a, b) => a + b, 0);
}

export async function reportStartupMetrics(
  env: StartupMetricsEnv = defaultEnv
): Promise<void> {
  try {
    const perf = env.getPerformance();
    const nav = env.getNavigator();
    const store = env.getLocalStorage();
    const cacheStorage = env.getCacheStorage();

    const [storage, stats, snapshots] = await Promise.all([
      readStorage(nav),
      cacheManager.getStorageStats(),
      storageManager.getSnapshotCacheStats(),
    ]);

    const heap = readHeap(perf);
    const timing = readNavTiming(perf);
    const hoursSinceLastOpen = readHoursSinceLastOpen(store);
    const [totalCachedItems, claimCount] = await Promise.all([
      readCachedItemCount(cacheStorage, stats.caches.names),
      claimQueueManager.count(),
    ]);
    if (heap.usedMb !== -1) {
      Sentry.metrics.gauge("memory.heap_used", heap.usedMb, {
        unit: "megabyte",
      });
    }
    if (heap.limitMb !== -1) {
      Sentry.metrics.gauge("memory.heap_limit", heap.limitMb, {
        unit: "megabyte",
      });
    }
    Sentry.metrics.gauge("storage.used", storage.usedMb, { unit: "megabyte" });
    Sentry.metrics.gauge("storage.quota", storage.quotaMb, {
      unit: "megabyte",
    });
    Sentry.metrics.gauge("sw.cached_items", totalCachedItems, { unit: "none" });
    Sentry.metrics.gauge("sw.cache_count", stats.caches.count, {
      unit: "none",
    });
    Sentry.metrics.gauge(
      "idb.pending_requests",
      stats.indexedDB.pendingRequests,
      { unit: "none" }
    );
    Sentry.metrics.gauge("idb.offline_data", stats.indexedDB.offlineData, {
      unit: "none",
    });
    Sentry.metrics.gauge("idb.snapshots.count", snapshots.count, {
      unit: "none",
    });
    Sentry.metrics.gauge(
      "idb.snapshots.bytes_mb",
      snapshots.totalBytes / 1024 / 1024,
      { unit: "megabyte" }
    );
    Sentry.metrics.gauge("idb.claim_queue", claimCount, { unit: "none" });
    if (timing.loadMs !== -1) {
      Sentry.metrics.gauge("page.load", timing.loadMs, { unit: "millisecond" });
    }
    if (timing.fetchMs !== -1) {
      Sentry.metrics.gauge("sw.fetch", timing.fetchMs, { unit: "millisecond" });
    }
    if (hoursSinceLastOpen !== -1) {
      Sentry.metrics.gauge(
        "session.hours_since_last_open",
        hoursSinceLastOpen,
        { unit: "hour" }
      );
    }

    store.setItem(LAST_APP_OPEN_KEY, String(Date.now()));
  } catch (error) {
    captureException(error);
  }
}
